import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { getPublicPlans } from '../contexts/db.Plans';
import { detectCurrency, getCurrencySymbol, getPlanPrice } from '../contexts/geolocation';
import '../styles/pages/PublicPlans.css';

function PublicPlans({ language }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState('EUR');

  const navigate = useNavigate();
  const t = translations[language];

  // Detect currency based on geolocation
  useEffect(() => {
    detectCurrency().then(setCurrency);
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);

        const plansResponse = await getPublicPlans();
        let normalizedPlans = [];
        if (Array.isArray(plansResponse)) {
          normalizedPlans = plansResponse;
        } else if (Array.isArray(plansResponse?.plans)) {
          normalizedPlans = plansResponse.plans;
        }

        setPlans(normalizedPlans);
      } catch (fetchError) {
        console.error('Error loading plans:', fetchError);
        setError(t.plansLoadError || 'Failed to load plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [language]);

  if (loading) {
    return (
      <div className="public-plans-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>{t.loadingPlans || 'Loading subscription plans...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-plans-container">
      <div className="public-plans-header">
        <h1>{t.publicPlansTitle || 'Choose Your Plan'}</h1>
        <p>{t.publicPlansSubtitle || 'Select the plan that best fits your needs and start your learning journey today.'}</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="public-plans-grid">
        {(Array.isArray(plans) ? plans : []).map((plan) => (
          <div key={plan.id} className="public-plan-card">
            <div className="free-trial-badge">
              {t.freeTrial || 'Free trial 3 days'}
            </div>
            <div className="plan-header">
              <h3>{plan.name}</h3>
              <div className="plan-price">
                <span className="currency">{getCurrencySymbol(getPlanPrice(plan, currency).currency)}</span>
                <span className="amount">{getPlanPrice(plan, currency).price}</span>
                <span className="period">
                  /{(() => {
                    if (plan.billing_cycle === '12_months') return t.twelveMonths || '12 months';
                    if (plan.billing_cycle === '3_months') return t.threeMonths || '3 months';
                    return t.monthly || 'month';
                  })()}
                </span>
              </div>
            </div>
            <div className="plan-description">
              {plan.description}
            </div>
            <ul className="plan-features">
              <li>{t.planFeature1 || 'Regular and personalized phone follow-up'}</li>
              <li>{t.planFeature2 || 'Access to all levels in the application'}</li>
              <li>{t.planFeature3 || 'Buy Heroes using your collected coins'}</li>
            </ul>
          </div>
        ))}
      </div>

      <div className="public-plans-cta">
        <h2>{t.publicPlansCtaTitle || 'Ready to Get Started?'}</h2>
        <p>{t.publicPlansCtaSubtitle || 'Create your account now and start your free trial.'}</p>
        <button
          className="get-started-btn"
          onClick={() => navigate('/register')}
        >
          {t.publicPlansGetStarted || 'Get Started'}
        </button>
      </div>
    </div>
  );
}

PublicPlans.propTypes = {
  language: PropTypes.string.isRequired,
};

export default PublicPlans;
